import { useId } from 'react'
import { createPortal } from 'react-dom'
import type { KeyboardEvent, MouseEvent, ReactElement, ReactNode } from 'react'
import { trapTabKey } from './focus-trap.ts'
import { useModalBehaviour } from './use-modal-behaviour.ts'

/**
 * A modal dialog that a keyboard can actually operate: focus trapped
 * inside it, Escape closes, focus returns to whatever opened it, the page
 * behind it does not scroll, and the whole thing is `aria-modal` and
 * labelled by its own heading.
 *
 * Not `<dialog showModal()>` — see the note at the top of `focus-trap.ts`;
 * jsdom has no `showModal`, so the native route would put the one part of
 * this component that matters permanently out of reach of the suite.
 *
 * Rendered through a portal to `document.body`. A `fixed` overlay is
 * positioned against the nearest ancestor with a `transform` or `filter`,
 * not the viewport, so a modal left inline will one day be clipped inside
 * whatever panel it was declared in — by someone adding a transition to
 * that panel, months later, with no idea they broke a dialog.
 */

export type ModalSize = 'sm' | 'md' | 'lg'

export interface ModalProps {
  isOpen: boolean
  /** Called by Escape, the close button and a click on the scrim. The
   * caller owns `isOpen`, so this component never closes itself. */
  onClose: () => void
  /** Also the dialog's accessible name, via `aria-labelledby`. A string
   * rather than a node because it has to work as a name. */
  title: string
  children: ReactNode
  /** The action row. Put the confirming control LAST — it is the one a
   * keyboard user reaches by tabbing forward from the body. */
  footer?: ReactNode
  size?: ModalSize
}

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-3xl',
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: ModalProps): ReactElement | null {
  const titleId = useId()
  const { dialogRef } = useModalBehaviour(isOpen)

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      // Stopped here so an Escape meant for the dialog does not also
      // clear a filter or close a panel on the page behind it.
      event.stopPropagation()
      onClose()
      return
    }
    const dialog = dialogRef.current
    if (event.key === 'Tab' && dialog !== null) trapTabKey(dialog, event)
  }

  function onScrimMouseDown(event: MouseEvent<HTMLDivElement>): void {
    // `mousedown` on the scrim ITSELF only. Using click would close the
    // dialog when a text selection that started inside it happens to end
    // on the scrim, which is an infuriating way to lose a half-typed form.
    if (event.target === event.currentTarget) onClose()
  }

  if (!isOpen) return null
  // SSR has no body to portal into. `isOpen` is client state in practice,
  // but a server render must not throw if that ever stops being true.
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      onMouseDown={onScrimMouseDown}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-overlay p-6"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // -1 so the dialog itself can hold focus on open: a screen reader
        // then reads the title and the body before landing on a control,
        // instead of announcing "Close, button" out of nowhere.
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={`mt-16 w-full rounded-lg border border-border bg-elevated ${SIZE_CLASSES[size]}`}
      >
        <ModalHeader titleId={titleId} title={title} onClose={onClose} />
        <div className="px-4 py-3 text-sm text-fg">{children}</div>
        {footer !== undefined && (
          <div className="flex items-center justify-end gap-2 border-t border-subtle px-4 py-2.5">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/** The title bar. The heading is what names the dialog, so its id is
 * passed in rather than generated here — `aria-labelledby` has to point
 * at this element and `Modal` is the one that owns the attribute. */
function ModalHeader({
  titleId,
  title,
  onClose,
}: {
  titleId: string
  title: string
  onClose: () => void
}): ReactElement {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-subtle px-4 py-2.5">
      <h2 id={titleId} className="text-sm font-semibold text-fg">
        {title}
      </h2>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="-mr-1 shrink-0 rounded-sm px-1 text-muted transition-colors hover:bg-surface hover:text-fg"
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
  )
}
